import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { faceMatchingService, PersonMatch, PersonHistoryFilters, ReferenceStatus, matchingError } from '../../services/face-matching.service';

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
};
const dateBoundary = (value: string, nextDay = false) => {
  const [year,month,day] = value.split('-').map(Number);
  // Calendar arithmetic includes the entire To date, including 23/25-hour DST days.
  return new Date(year,month-1,day+(nextDay ? 1 : 0)).toISOString();
};

const PersonMatchHistory: React.FC<{ personId: number }> = ({ personId }) => {
  const [items, setItems] = useState<PersonMatch[]>([]);
  const [references, setReferences] = useState<ReferenceStatus[]>([]);
  const [error, setError] = useState('');
  const [referenceError, setReferenceError] = useState('');
  const [canManageReferences, setCanManageReferences] = useState(false);
  const [retryingPhoto, setRetryingPhoto] = useState<number | null>(null);
  const referenceGeneration = useRef(0);
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(localToday);
  const [to, setTo] = useState(localToday);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [applied, setApplied] = useState<PersonHistoryFilters | null>(null);
  const historyRequest = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const sectionId = useId();
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    setItems([]); setReferences([]); setError(''); setReferenceError('');
    setCanManageReferences(false); setRetryingPhoto(null);
    setOpen(false); setFrom(localToday()); setTo(localToday());
    setLoading(false); setLoaded(false); setPage(1); setHasNext(false); setApplied(null);
    const load = async () => {
      try {
        const photos = await faceMatchingService.references(personId, controller.signal);
        if (active) { setReferences(photos.items); setCanManageReferences(!!photos.can_manage); setReferenceError(''); }
      } catch (error) { if (active) setReferenceError(matchingError(error)); }
      if (active) timer = setTimeout(() => {
        if (document.hidden) timer = setTimeout(load,15000);
        else void load();
      },15000);
    };
    void load();
    return () => {
      active = false; clearTimeout(timer); controller.abort(); referenceGeneration.current++;
      historyRequest.current?.abort(); generation.current++;
    };
  }, [personId]);

  const retryPhoto = async (photoId: number) => {
    const current = referenceGeneration.current;
    setRetryingPhoto(photoId); setReferenceError('');
    try {
      await faceMatchingService.retryReference(personId, photoId);
      if (current !== referenceGeneration.current) return;
      setReferences(items => items.map(photo => photo.face_image_id === photoId
        ? { ...photo, status:'pending', last_error:undefined } : photo));
    } catch (error) {
      if (current === referenceGeneration.current) setReferenceError(matchingError(error));
    } finally {
      if (current === referenceGeneration.current) setRetryingPhoto(null);
    }
  };

  const clearResults = () => {
    historyRequest.current?.abort(); generation.current++;
    setItems([]); setError(''); setLoaded(false); setLoading(false); setApplied(null); setPage(1); setHasNext(false);
  };
  const loadHistory = async (filters: PersonHistoryFilters) => {
    historyRequest.current?.abort();
    const controller = new AbortController();
    historyRequest.current = controller;
    const current = ++generation.current;
    setLoading(true); setError('');
    try {
      const result = await faceMatchingService.history(personId, filters, controller.signal);
      if (controller.signal.aborted || current !== generation.current) return;
      setItems(result.items); setPage(filters.page); setHasNext(result.has_next); setLoaded(true); setApplied(filters);
    } catch (error) {
      if (!controller.signal.aborted && current === generation.current) setError(matchingError(error));
    } finally {
      if (!controller.signal.aborted && current === generation.current) setLoading(false);
    }
  };
  const search = (event: React.FormEvent) => {
    event.preventDefault();
    if (!from || !to || from > to) {
      setError('Choose both dates, with From on or before To.'); return;
    }
    void loadHistory({ start_time:dateBoundary(from), end_time:dateBoundary(to,true), page:1, size:10 });
  };
  return <Card padding="md">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h3 className="font-semibold text-text-primary">Enrollment &amp; event matches</h3>
        <p className="text-xs text-text-muted mt-1">Search this person’s event history by date.</p></div>
      <Button type="button" size="sm" variant="outline" aria-expanded={open} aria-controls={sectionId}
        rightIcon={open ? <ChevronUp size={16} /> : <ChevronDown size={16} />} onClick={() => {
          if (open) { historyRequest.current?.abort(); generation.current++; setLoading(false); }
          setOpen(!open);
        }}>{open ? 'Hide event history' : 'Show event history'}</Button>
    </div>
    {referenceError && <p role="alert" className="text-sm text-status-danger mt-3">{referenceError}</p>}
    {!!references.length && <div className="flex flex-wrap gap-2 mt-3">{references.map(photo => <div key={photo.face_image_id} className="flex items-center gap-2 rounded bg-surface-secondary px-3 py-1 text-xs text-text-secondary">
      <span>Photo #{photo.face_image_id}: {photo.status === 'completed' ? 'Indexed' : photo.status.replace(/_/g, ' ')}{photo.last_error ? ` — ${photo.last_error}` : ''}</span>
      {canManageReferences && ['failed','not_queued'].includes(photo.status) && <Button type="button" size="sm" variant="outline" disabled={retryingPhoto !== null}
        isLoading={retryingPhoto === photo.face_image_id} onClick={() => void retryPhoto(photo.face_image_id)}>Retry photo #{photo.face_image_id}</Button>}
    </div>)}</div>}
    {open && <section id={sectionId} aria-label="Person event history" className="mt-4 space-y-3 border-t border-border-default pt-4">
      <form onSubmit={search} className="flex flex-wrap items-end gap-3">
        <Input type="date" label="From" required value={from} onChange={event => { clearResults(); setFrom(event.target.value); }} />
        <Input type="date" label="To" required value={to} onChange={event => { clearResults(); setTo(event.target.value); }} />
        <Button type="submit" size="sm" isLoading={loading} leftIcon={<Search size={15} />}>Search matches</Button>
      </form>
      <p className="text-xs text-text-muted">Dates and event times use your local timezone. Up to 10 matches per page.</p>
      {error && <p role="alert" className="text-sm text-status-danger">{error}</p>}
      {loading && <p role="status" className="text-sm text-text-muted">Loading event matches…</p>}
      {!loading && !loaded && !error && <p className="text-sm text-text-muted">Choose a date range and select Search matches.</p>}
      {!loading && loaded && !items.length && <p className="text-sm text-text-muted">No event matches in this date range.</p>}
      {!!items.length && <div className="max-h-80 overflow-y-auto space-y-2" aria-busy={loading}>{items.map(item => <a key={item.id} href={`/events/${item.event_id}/view`} className="block rounded-lg border border-border-default p-3 hover:bg-surface-hover">
        <div className="flex flex-wrap justify-between gap-2 text-sm text-text-primary"><span>Event #{item.event_id} · {item.location_name}</span><span>{item.review_status}</span></div>
        <p className="text-xs text-text-muted mt-1">{item.event_time ? new Date(item.event_time).toLocaleString() : 'Event time unavailable'} · {item.event_tag} · Similarity {item.similarity.toFixed(3)}</p>
      </a>)}</div>}
      {loaded && <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-text-muted">Page {page} · {items.length} {items.length === 1 ? 'match' : 'matches'}</p>
        <div className="flex gap-2"><Button type="button" size="sm" variant="outline" disabled={loading || page === 1} onClick={() => applied && void loadHistory({ ...applied,page:page-1 })}>Previous</Button>
          <Button type="button" size="sm" variant="outline" disabled={loading || !hasNext} onClick={() => applied && void loadHistory({ ...applied,page:page+1 })}>Next</Button></div>
      </div>}
    </section>}
  </Card>;
};
export default PersonMatchHistory;
